import { useCallback, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { CampaignSearch } from './search';
import { pageParamKey, type PageNamespace } from './pageParamKeys';
import { useCampaignPageParam } from './useCampaignPageParam';
import { useCampaignPageSizeParam } from './useCampaignPageSizeParam';

/** The server caps `limit` here; no size above it is requested or offered. */
export const PAGED_TABLE_MAX_SIZE = 10000;

export interface PagedTableUrlState {
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
}

/** Page and size of one table, URL-owned under its own namespace. */
export function usePagedTableUrlState(namespace: PageNamespace): PagedTableUrlState {
  const [page, setPage] = useCampaignPageParam(namespace);
  const [pageSize, setPageSize] = useCampaignPageSizeParam(PAGED_TABLE_MAX_SIZE, namespace);
  return { page, setPage, pageSize, setPageSize };
}

/** A page past the end (a hand-edited URL, or rows deleted from the last page)
 *  comes back empty with the real total; move to the last page that exists.
 *  Skipped while the shown data is still the previous query's placeholder. */
export function useSnapPastEnd(
  state: Pick<PagedTableUrlState, 'page' | 'setPage'>,
  loaded: { totalPages: number } | undefined,
  isPlaceholderData: boolean,
): void {
  const { page, setPage } = state;
  const lastPage = loaded ? Math.max(1, loaded.totalPages) : undefined;
  useEffect(() => {
    if (lastPage === undefined || isPlaceholderData) return;
    if (page > lastPage) setPage(lastPage);
  }, [page, lastPage, isPlaceholderData, setPage]);
}

/** Sends the given tables back to their first page in one navigation (two
 *  separate navigations would each start from the same stale search). */
export function useResetPages(namespaces: readonly PageNamespace[]): () => void {
  const navigate = useNavigate();
  const keys = namespaces.map((ns) => pageParamKey(ns)).join(',');
  return useCallback(
    () =>
      navigate({
        to: '.',
        search: ((prev: Partial<CampaignSearch>) => ({
          ...prev,
          ...Object.fromEntries(keys.split(',').map((key) => [key, undefined])),
        })),
        resetScroll: false,
      } as never),
    [navigate, keys],
  );
}
